import { useEffect, useState } from 'react';
import { MsalProvider, MsalAuthenticationTemplate } from '@azure/msal-react';
import {
  InteractionType,
  EventType,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { loginRequest, msalInstance } from './msalConfig';
import TeamsSDK from '@/teams/TeamsSDK';

// MSAL real SOLO cuando VITE_AUTH_ENABLED=true. Si no, modo demo (sin login real), igual que hoy.
// Excluyente con Easy Auth: al encender MSAL hay que APAGAR Easy Auth en el App Service.
const AUTH_ENABLED = import.meta.env.VITE_AUTH_ENABLED === 'true';

interface Props {
  children: React.ReactNode;
}

export function AuthProvider({ children }: Props) {
  const [ready, setReady] = useState(false);
  const [inTeams, setInTeams] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await msalInstance.initialize();
      // Procesa el #code del redirect de login -> intercambia por tokens -> cachea la cuenta.
      // SIN esto el #code se queda pegado en la URL y nunca hay sesión -> token vacío -> 401.
      const redirectResult = await msalInstance.handleRedirectPromise();
      if (redirectResult?.account) msalInstance.setActiveAccount(redirectResult.account);
      // Restaurar cuenta activa (tras un redirect de login o una recarga)
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && !msalInstance.getActiveAccount()) msalInstance.setActiveAccount(accounts[0]);
      msalInstance.addEventCallback(event => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const account = (event.payload as AuthenticationResult).account;
          if (account) msalInstance.setActiveAccount(account);
        }
      });
      // Detectar Teams: dentro del tab el redirect MSAL se bloquea (iframe) -> usar Teams SSO.
      let teams = false;
      if (window !== window.top) {
        try {
          await Promise.race([
            TeamsSDK.initialize(),
            new Promise<never>((_, r) => setTimeout(() => r(new Error('teams-timeout')), 2000)),
          ]);
          const ctx = await TeamsSDK.getContext();
          if (ctx) { teams = true; TeamsSDK.markInTeams(); }
        } catch { /* no estamos en Teams */ }
      }
      if (!cancelled) { setInTeams(teams); setReady(true); }
    })().catch(() => {
      if (!cancelled) setReady(true); // no bloquear el render si algo falla
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ready) return null;

  // Modo demo (sin MSAL) O dentro de Teams: no forzar el loginRedirect.
  // En Teams la auth es por SSO (getAuthToken en client.ts); el redirect rompería en el iframe.
  if (!AUTH_ENABLED || inTeams) {
    return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
  }

  // Navegador standalone con MSAL on: si no hay sesión, MsalAuthenticationTemplate dispara el
  // loginRedirect automáticamente (redirige a Microsoft y vuelve). Con sesión, renderiza la app.
  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
      >
        {children}
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}
